import {
  dateFromTimeSelector,
  todayAtMidnight,
  todayAtNow
} from "@/stores/helpers/machineStatusHelper";
import { calculateEfficiencyPerc, calculateAvailabilityPerc } from "@/stores/Utils";
import MachineStatusApi from "@/api/machineStatusApi";
import MachinesApi from "@/api/machinesApi";
import MachineShiftsApi from "@/api/machineShiftsApi";
import MachineProcutionsApi from "@/api/machineProductionsApi";
import MachineAlarmsApi from "@/api/machineAlarmsApi";
import MachineToolingApi from "@/api/machineToolingApi";
import MachineLicense from "@/api/machineLicense";
import MachineEnvironmentalSensorsApi from "@/api/machineEnvironmentalSensorsApi";
import { DateTime } from "luxon";
import streamApi from "@/api/streamApi";
import { roundValue } from "../../components/analytics/helpers";
import { DateHelper } from "@/utility/dateHelper.js";
import { date } from "@/components/mixins/date";

const apiMS = new MachineStatusApi();
const apiM = new MachinesApi();
const apiMShifts = new MachineShiftsApi();
const apiMEnvSensors = new MachineEnvironmentalSensorsApi();
const apiMProductions = new MachineProcutionsApi();
const apiMAlarms = new MachineAlarmsApi();
const apiMTooling = new MachineToolingApi();
const apiMLicense = new MachineLicense();

export const shiftShortKpi = {
  APPLIED_EDGE_LENGTH: "AEL",
  CYCLE_COUNT: "CC",
  LINEAR_FOOTAGE: "LF",
  METERS_WORKED: "MW",
  FEETS_WORKED: "FW",
  PART_COUNT: "PC",
  PART_MOVED: "PM",
  RUNNING_MACHINE: "RM",
  SPINDLE_TIME: "ST",
  VOLUME_MOVED: "VM",
  WOODCUT_VOLUME: "WV"
};
export const shiftUdmKpi = (value, translations) => {
  return {
    APPLIED_EDGE_LENGTH: "(m)",
    CYCLE_COUNT: `(${translations("misc.sides").toLowerCase()})`,
    LINEAR_FOOTAGE: "(m)",
    METERS_WORKED: "(m)",
    FEETS_WORKED: "(ft)",
    PART_COUNT: `(${translations("misc.pieces").toLowerCase()})`,
    PART_MOVED: `(${translations("misc.pieces").toLowerCase()})`,
    RUNNING_MACHINE: "(m)",
    SPINDLE_TIME: "",
    VOLUME_MOVED: "(m³)",
    WOODCUT_VOLUME: "(m³)",
    "-": "",
    undefined: ""
  }[value || undefined];
};

export const kpiFields = {
  APPLIED_EDGE_LENGTH: "appliedEdgeLength",
  CYCLE_COUNT: "cycleCount",
  LINEAR_FOOTAGE: "linearFootage",
  FEETS_WORKED: "feetsWorked",
  METERS_WORKED: "metersWorked",
  PART_COUNT: "partCount",
  PART_MOVED: "partMoved",
  RUNNING_MACHINE: "runningMachine",
  SPINDLE_TIME: "spindleTime",
  VOLUME_MOVED: "volumeMoved",
  WOODCUT_VOLUME: "woodcutVolume",
  WOOD_VOLUME: "woodVolume"
};

export const getMachineStatusHistoricalAggregated = (machine, interval) => {
  const from = dateFromTimeSelector(interval);
  const to = todayAtMidnight();

  return apiMS.getMachineStatusHistoricalAggregated(machine.Id, interval, from, to);
};

export const getUserMachines = async () => {
  let machines = [];
  // Get All Machines
  const res = await apiM.getMachines({ skip: 0, limit: 1 });
  machines = res.result;
  if (res.total > res.result.length) {
    const res2 = await apiM.getMachines({ skip: 1, limit: res.total });
    machines = machines.concat(res2.result);
  }
  return machines;
};

export const getMachineConsumptions = async (machineId, from, to) => {
  try {
    const { result } = await apiM.getMachineConsumptions(machineId, from, to);
    return result;
  } catch (e) {
    console.error("[Error] machine consumptions error:", e);
    return [];
  }
};

export const getMachineStatusAggregated = (machine, interval) => {
  const from = dateFromTimeSelector(interval);
  const to = todayAtNow();

  return apiMS.getMachineStatusAggregated(machine.Id, from, to);
};

export const getMachineStatusDay = machine => {
  const from = DateTime.local()
    .set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
    .toJSDate()
    .toISOString();
  const to = date.methods.objDateToday().toISOString();
  const last = true;
  const params = {
    ...{ from },
    ...{ to },
    ...{ last }
  };
  return apiMS.getMachineStatusFromTo(machine && machine.Id, params);
};

export const getMachineStatusLastByMachineIds = machineIds => {
  return apiMS.getMachineStatusLastByMachineIds(machineIds);
};

export const getMachineStatusLastByMachineId = machineId => {
  return apiMS.getMachineStatusLastByMachineId(machineId);
};

export async function getMachinesByGroup({ groupId, skip, limit }) {
  const params = {
    ...{ skip },
    ...{ limit }
  };
  const res = await apiM.getMachinesByGroupId(groupId, params);
  return res;
}

export async function getShiftKpis(machineFamily) {
  return await apiMShifts.getShiftKpis(machineFamily);
}

export async function getMachineShifts(machineId) {
  return await apiMShifts.getMachineShifts(machineId);
}

export async function getCurrentMachinesShift(machines) {
  return await apiMShifts.getCurrentMachinesShift(machines);
}

export async function saveMachineShifts(machineId, shifts) {
  return await apiMShifts.saveMachineShifts(machineId, shifts);
}

export async function getLicense(machId) {
  return await apiMLicense.getLicense(machId);
}

export async function activationeLicense(machineId) {
  return await apiMLicense.activationeLicense(machineId);
}

export async function getCurrentMachineShift(machine) {
  try {
    if (machine.Device) {
      const currentShift = await apiMShifts.getCurrentMachineShift(machine.Id);
      return currentShift
        ? {
            ...currentShift,
            timezone: machine.Country.Timezone,
            name: `Shift ${currentShift.Number}`,
            shortKpi: currentShift.Goal ? shiftShortKpi[currentShift.Goal.Kpi] : "-"
          }
        : null;
    } else {
      return null;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function getShiftUser(machine) {
  try {
    if (machine.Device) {
      const res = await streamApi.getQueryDataLast(machine.Id, "Machine/Hmi/User");
      const response = res && res.data && res.data.v ? res.data.v : "-";
      return !!response && response !== "null" ? response : "-";
    } else {
      return "-";
    }
  } catch (e) {
    console.error(e);
    return "-";
  }
}

export const shiftsHistory = async (
  machineId,
  from,
  to,
  skip,
  limit = 1,
  hasConsumptions = undefined
) => {
  const search = {
    skip,
    limit
  };

  if (from) {
    search.from = DateTime.fromISO(from).toFormat("yyyy-LL-dd");
  }
  if (to) {
    search.to = DateTime.fromISO(to).toFormat("yyyy-LL-dd");
  }
  let data = null;
  try {
    data = await apiMShifts.getShiftsHistorical(machineId, search);

    if (!data.error && limit === 1 && data.total > limit) {
      search.skip = limit;
      search.limit = data.total;
      const otherData = await apiMShifts.getShiftsHistorical(machineId, search);
      data.result = [...data.result, ...otherData.result];

      data.count = data.total;
    }
  } catch (e) {
    console.error("[Error] shiftsHistory error:", e);
    data = {
      result: [],
      count: 0
    };
  }

  if (hasConsumptions) {
    const resultWithConsumptions = await Promise.all(
      data.result.map(async record => {
        const consumptions = await getMachineConsumptions(
          machineId,
          new Date(record.StartDateTime).toISOString(),
          new Date(record.EndDateTime).toISOString()
        );
        const energyConsumptionDuringTheShift = consumptions.reduce((accumulator, item) => {
          for (const consume of item.consumptions) {
            for (const measure of consume.measures) {
              for (const value of measure.values) {
                if (
                  value.aggregation.toLowerCase() === "cumulated" &&
                  value.unit.toLowerCase() === "wh"
                ) {
                  accumulator = accumulator + value.amount;
                }
              }
            }
          }
          return accumulator;
        }, 0);

        let value = Number(energyConsumptionDuringTheShift);
        let unit = "Wh";
        let factor = 1;
        if (value > 10000) {
          unit = "kWh";
          factor = 1000;
          value = value / factor;
          value = value.toFixed(2) || 0;
        } else {
          value = value.toFixed(0) || 0;
        }

        return {
          ...record,
          EnergyConsumptionDuringTheShift: value,
          unit
        };
      })
    );
    data.result = resultWithConsumptions;
  }

  return data;
};

export const getEnvVars = async (machineId, from, to) => {
  const sensors = ["humidity", "temperature", "pressure"];
  const sources = ["electricalCabinet", "environment"];

  const search = {
    sensors: sensors.join(","),
    sources: sources.join(","),
    from,
    to
  };

  let data = null;
  try {
    data = await apiMEnvSensors.getSensorData(machineId, search);
  } catch (e) {
    console.error("[Error] getEnvVars error:", e);
    data = {
      result: [],
      count: 0
    };
  }
  return data;
};

export const getDailyGoals = async (machineId, skip, from, to) => {
  const search = {
    skip,
    limit: 3
  };

  if (from) {
    search.from = DateTime.fromISO(from).toFormat("yyyy-LL-dd");
  }
  if (to) {
    search.to = DateTime.fromISO(to).toFormat("yyyy-LL-dd");
  }
  let data;

  try {
    data = await apiMShifts.getShiftsHistorical(machineId, search);
  } catch (e) {
    console.error("[Error] getDailyGoals error:", e);
    data = {
      result: [],
      count: 0
    };
  }
  return data;
};

export const machineProductions = async (machineId, queryParams) => {
  const from = DateTime.fromISO(queryParams.from).startOf("day");
  let to = DateTime.fromISO(queryParams.to).endOf("day");
  const now = DateTime.now();

  if (to > now) {
    to = now;
  }
  const newQuery = {
    ...queryParams,
    from: from.toJSDate().toISOString(),
    to: to.toJSDate().toISOString()
  };

  const data = await apiMProductions.getMachineProductions(machineId, newQuery);

  return data;
};

export const getProductionTotalQuantity = parts => {
  let totQty = 0;
  parts.forEach(part => {
    try {
      totQty += part.Qty ? Number(part.Qty) : 0;
    } catch (e) {
      console.error(e);
    }
  });
  return totQty;
};

export const getMachineProductionExtraKeys = productions => {
  return productions.reduce((extraKeys, { Extra }) => {
    Object.keys(Extra).forEach(key => {
      extraKeys.add(key);
    });
    return extraKeys;
  }, new Set());
};

export const getMachineProductionCycleTime = (from, to, status) => {
  const objTime = date.methods.diffHoursMinutesSeconds(from, to);
  let cycleTimeToString = "";

  cycleTimeToString += objTime.hours > 0 ? Math.round(objTime.hours) + "h " : "";
  cycleTimeToString +=
    objTime.minutes > 0 || objTime.hours > 0 ? Math.round(objTime.minutes) + "m " : "";
  cycleTimeToString += objTime.seconds >= 0 ? Math.round(objTime.seconds) + "s" : "";

  return status === "ELAPSED" ? "N.D." : cycleTimeToString;
};

export const fillMissingProductionsCSVFields = (productions, extraKeys) => {
  for (let i = 0; i < productions.length; i++) {
    const keys = Object.keys(productions[i].Extra);
    for (let j = 0; j < extraKeys.length; j++) {
      if (!keys.includes(extraKeys[j])) {
        productions[i].Extra[extraKeys[j]] = null;
      }
    }
  }
  return productions;
};

export const productionsFilteringAndOrdering = productions => {
  return productions.map(production => {
    // eslint-disable-next-line no-unused-vars
    const sortedExtra = Object.keys(production.Extra)
      .sort()
      .reduce((acc, key) => {
        acc[key] = production.Extra[key];
        return acc;
      }, {});
    delete production.Id;
    delete production.Extra;
    delete sortedExtra.Parts;
    return {
      ...production,
      ...sortedExtra
    };
  });
};

export const machineProductionDetails = async (machine, productionId, from, to) => {
  const data = await apiMProductions.getMachineProductionDetails(machine.Id, productionId, {
    from,
    to
  });
  from = DateTime.fromISO(data.result.StartTime).minus({ seconds: 5 }).toJSDate().toISOString();
  to = DateTime.fromISO(data.result.UpdateTime).plus({ seconds: 5 }).toJSDate().toISOString();
  const timeline = await apiMS.getMachineStatusFromTo(machine.Id, { from, to });
  data.result.Timeline = timeline;
  const statusAggr = await apiMS.getMachineStatusAggregated(machine.Id, from, to);
  if (statusAggr) {
    data.result.Efficiency = calculateEfficiencyPerc(statusAggr);
    data.result.Availability = calculateAvailabilityPerc(statusAggr);
  }
  if (data.result.Extra.Parts) {
    data.result.Parts = data.result.Extra.Parts;
    data.result.Extra.TotalProducedPieces = getProductionTotalQuantity(data.result.Extra.Parts);
    delete data.result.Extra.Parts;
  }
  return data.result;
};

export const subscribeToAlarms = async ({ machineId, callback }) => {
  streamApi.watchCpData(machineId, "Alarms", response => {
    const alarmReceived = response.data;
    console.log("Alarm received>>>>", alarmReceived);
    callback({
      ...alarmReceived,
      code: alarmReceived.alarmCode,
      state: alarmReceived.activeState,
      timestamp: new Date(alarmReceived.ts).toISOString()
    });
  });
};

export const machineAlarms = async machineId => {
  const res = await apiMAlarms.getMachineAlarms(machineId);

  // mock documents
  /*res.result[0]["documents"]["translations"][0] = [
    {
      title: "my-test-file",
      link:
        "https://tst.api.maestroconnect.scmgroup.com/storage-api/contents/1620393556071-Accord25fx - Master CE.pdf",
      locale: "it-IT",
      type: "application/pdf"
    }
  ];*/

  return res.result;
};

export const machineAlarmsHistoryForAlarmCode = async (
  machineId,
  alarmCode,
  skip = 0,
  limit = 1,
  after = "",
  before = ""
) => {
  const search = {
    skip,
    limit,
    after,
    before
  };
  const data = await apiMAlarms.getMachineAlarmsHistory(machineId, alarmCode, search);

  return data;
};

export const machineAlarmsHistoryForAlarmCodeCSV = async (
  machineId,
  alarmCode,
  after = "",
  before = ""
) => {
  let skip = 0;
  let data = [];
  const limit = 100;

  const search = {
    skip,
    limit,
    after,
    before
  };
  const { total, result } = await apiMAlarms.getMachineAlarmsHistory(machineId, alarmCode, search);

  data = result;

  while (total > limit * (skip + 1)) {
    skip++;
    const res = await apiMAlarms.getMachineAlarmsHistory(machineId, alarmCode, {
      skip: skip * limit,
      limit,
      after,
      before
    });
    data = [...data, ...res.result];
  }

  return {
    result: data,
    total
  };
};

export const machineTooling = async (machineId, filters) => {
  const search = {
    ...filters
  };

  const data = await apiMTooling.getMachineTooling(machineId, search);

  return data;
};

export const getToolByIdAndMachineId = async (machineId, toolId) => {
  const result = [];
  const tool = await apiMTooling.getToolByIdAndMachineId(machineId, encodeURIComponent(toolId));
  if (tool) {
    result.push(tool.result);
    return { count: 1, total: 1, result };
  }
  return result;
};

export const machineToolingDetails = async (machine, toolId) => {
  const data = await apiMTooling.getMachineToolingDetails(machine.Id, encodeURIComponent(toolId));
  return data.result;
};

export const patchTool = async (machineId, toolId, params) => {
  const data = await apiMTooling.patchTool(machineId, encodeURIComponent(toolId), params);
  return data;
};

export const calculateAvailabilityAndEfficiency = aggregateDay => {
  const result = ["FAIL", "EXE", "HANDLING", "READY"].reduce((acc, el) => {
    acc[el] = Math.floor(Number(aggregateDay[el])) || 0; // campi mancanti valgono zero
    return acc;
  }, {});

  const efficiency =
    result["EXE"] > 0 || result["FAIL"] > 0
      ? Math.round((Number(result["EXE"]) / (Number(result["EXE"]) + Number(result["FAIL"]))) * 100)
      : 0;

  const availability =
    result["EXE"] > 0 || result["FAIL"] > 0 || result["READY"] > 0 || result["HANDLING"] > 0
      ? Math.round(
          ((Number(result["EXE"]) + Number(result["READY"]) + Number(result["HANDLING"])) /
            (Number(result["EXE"]) +
              Number(result["READY"]) +
              Number(result["HANDLING"] + Number(result["FAIL"])))) *
            100
        )
      : 0;

  return {
    efficiency,
    availability
  };
};

export const convertMMintoM = value => {
  return value / 1000;
};

export const isKpiUdmInMillimeter = kpi => {
  return ["LINEAR_FOOTAGE", "APPLIED_EDGE_LENGTH"].indexOf(kpi) > -1;
};

export const isKpiUdmInSeconds = kpi => {
  return ["SPINDLE_TIME"].indexOf(kpi) > -1;
};

export const evalKpiAndValue = (kpi, value) => {
  if (isKpiUdmInMillimeter(kpi)) {
    return roundValue(convertMMintoM(value), 3);
  } else if (isKpiUdmInSeconds(kpi)) {
    const res = value > 0 ? DateHelper.getFancyTimeFormat(value) : "0h";
    return res;
  } else {
    return roundValue(value, 3);
  }
};

function getLastActivation(machine) {
  return (
    machine &&
    machine.Activations &&
    machine.Activations.length > 0 &&
    machine.Activations[machine.Activations.length - 1]
  );
}

/**
 * Returns the status of activation based on StartDate and EndDate.
 * @param {*} activation activation to check
 * @returns 0: not active
 *          -1: activation not yet started
 *          1: active
 *          2: expired
 */
function activationStatus(activation) {
  const now = DateTime.now().valueOf();
  const startDate = DateTime.fromISO(activation && activation.StartDate).valueOf();
  const endDate = DateTime.fromISO(activation && activation.EndDate).valueOf();
  const isBefore = now < startDate;
  const isAfter = now > endDate;
  const status = isBefore ? -1 : isAfter ? 2 : 1;

  return activation ? status : 0;
}

export function isFreeTrialExpired(machine) {
  const lastActivation = getLastActivation(machine);
  const expired = activationStatus(lastActivation) === 2;

  return (
    machine &&
    lastActivation &&
    machine.FreeTrialReady &&
    lastActivation.Type === "FREE_TRIAL" &&
    expired
  );
}

export function isFreeTrialEnabled(machine) {
  const lastActivation = getLastActivation(machine);

  return (
    machine && lastActivation && machine.FreeTrialReady && lastActivation.Type === "FREE_TRIAL"
  );
}

export function isFullLicenseEnabled(machine) {
  const lastActivation = getLastActivation(machine);
  const isActive = activationStatus(lastActivation) === 1;

  return machine && lastActivation && lastActivation.Type === "FULL" && isActive;
}

export function isLicenseEnabled(machine) {
  const lastActivation = getLastActivation(machine);
  const isActive = activationStatus(lastActivation) === 1;

  return isActive;
}

export function noActivationsFreeTrialReady(machine) {
  return (
    machine && machine.FreeTrialReady && machine.Activations && machine.Activations.length === 0
  );
}

export function subscriptionManagementDestinationURI(machineSerial) {
  return `/subscriptions-management/machine-activation?ms=${encodeURIComponent(machineSerial)}`;
}

export function hasImOrSensorKitType(machine) {
  return (
    machine?.Properties?.KitType?.toLowerCase() === "im" ||
    machine?.Properties?.KitType?.toLowerCase() === "sensor"
  );
}
